import { useEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ArrowRight, CheckCircle2, Crown, Loader2, X } from "lucide-react";
import { toast } from "sonner";

import { subscribeWaitlist } from "../lib/api";

const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const ALLOWED_SOURCES = new Set([
  "organic", "instagram", "reddit", "twitter", "x", "tiktok",
  "facebook", "linkedin", "youtube", "google", "newsletter",
  "blog", "partner", "referral", "press", "direct",
]);

function normaliseSource(raw) {
  const s = (raw || "").trim().toLowerCase().slice(0, 40);
  return ALLOWED_SOURCES.has(s) ? s : "organic";
}

/**
 * FoundingClaimModal — premium founding-spot reservation dialog.
 *
 * Uses Radix Dialog primitives directly (instead of the shadcn wrapper) so
 * we can override the default harsh dark overlay with a soft backdrop-blur
 * scrim that matches BlendSafe's calm aesthetic.
 *
 * Built-in: ESC to close, outside-click to close, focus trap, fade+scale
 * animation. Submits to the same /api/waitlist/subscribe endpoint.
 */
export default function FoundingClaimModal({ open, onOpenChange, source = "organic", onSuccess }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | duplicate
  const inputRef = useRef(null);
  const safeSource = normaliseSource(source);

  // Reset state every time the modal opens so re-opening always shows a
  // clean form.
  useEffect(() => {
    if (open) {
      setStatus("idle");
      setEmail("");
      setFirstName("");
      // Defer focus until Radix has finished mounting + animating.
      const t = setTimeout(() => inputRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  const isValid = firstName.trim().length > 0 && EMAIL_RE.test(email.trim());
  const submitting = status === "submitting";
  const settled = status === "success" || status === "duplicate";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || settled) return;
    if (!isValid) {
      toast.warning("Add your first name and a valid email", {
        description: "We use your name to greet you in the confirmation email.",
      });
      return;
    }
    setStatus("submitting");
    try {
      const res = await subscribeWaitlist({
        email: email.trim().toLowerCase(),
        first_name: firstName.trim(),
        source: safeSource,
      });
      const isDup = (res?.message || "").toLowerCase().includes("already");
      const tier = res?.tier || "founding";
      setStatus(isDup ? "duplicate" : "success");
      if (!isDup) {
        // Real (non-duplicate) reservation — let callers track conversions.
        try { onSuccess?.(res); } catch { /* noop */ }
      }
      if (isDup) {
        toast.message("You're already on the waitlist", {
          description: "No need to sign up again — check your inbox for your confirmation email.",
        });
      } else if (tier === "founding") {
        toast.success(`You're founding member #${res.founding_number ?? "—"}`, {
          description: "Check your email to confirm your spot.",
        });
      } else {
        toast.success("You're on the list", {
          description: "Check your email to confirm.",
        });
      }
      // Auto-close after a short celebratory pause.
      setTimeout(() => onOpenChange?.(false), 1600);
    } catch (err) {
      setStatus("idle");
      toast.error("Something went wrong", {
        description: err?.response?.data?.detail || "Check your connection and try again — nothing was saved.",
      });
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-[#1f3d2e]/30 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          data-testid="founding-claim-modal"
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-[rgba(168,122,50,0.22)] bg-cream-50 p-6 sm:p-7 shadow-floating duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          style={{
            boxShadow:
              "0 1px 2px rgba(0,0,0,0.04), 0 24px 60px -20px rgba(31,61,46,0.35), 0 12px 28px -16px rgba(168,122,50,0.20)",
          }}
        >
          {/* Eyebrow badge */}
          <div className="flex items-center justify-between">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.22em] text-[#a87a32]"
              style={{
                background: "linear-gradient(135deg,#f4e8d0 0%,#e8d4a8 100%)",
                border: "1px solid rgba(168,122,50,0.3)",
              }}
            >
              <Crown className="w-3 h-3" /> Founding member
            </span>
            <DialogPrimitive.Close
              data-testid="founding-claim-close"
              aria-label="Close"
              className="inline-flex items-center justify-center w-9 h-9 rounded-full text-ink-mute hover:text-ink hover:bg-cream-100 transition-colors focus:outline-none focus:ring-2 focus:ring-ink/20"
            >
              <X className="w-4 h-4" />
            </DialogPrimitive.Close>
          </div>

          {/* Headline */}
          <DialogPrimitive.Title
            data-testid="founding-claim-headline"
            className="mt-4 text-[22px] sm:text-[24px] font-extrabold tracking-[-0.018em] leading-[1.15] text-ink"
          >
            Reserve your Founding Member spot
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="mt-2 text-[13.5px] text-ink-soft leading-snug">
            One truthful perk: <strong className="text-ink">Pro free for 12 months when payments launch.</strong>
          </DialogPrimitive.Description>

          {/* Form */}
          <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-2.5">
            <input
              ref={inputRef}
              type="text"
              maxLength={30}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={submitting || settled}
              placeholder="First name"
              data-testid="founding-claim-firstname"
              className="h-12 rounded-xl border border-surface-line bg-white px-4 text-[15px] text-ink placeholder:text-ink-light focus:outline-none focus:ring-4 focus:ring-cream-100 focus:border-ink-mute transition-all disabled:opacity-60"
            />
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting || settled}
              placeholder="you@email.com"
              data-testid="founding-claim-email"
              className="h-12 rounded-xl border border-surface-line bg-white px-4 text-[15px] text-ink placeholder:text-ink-light focus:outline-none focus:ring-4 focus:ring-cream-100 focus:border-ink-mute transition-all disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={submitting || settled}
              data-testid="founding-claim-submit"
              className="h-12 px-5 rounded-xl text-cream-50 text-[14px] font-semibold transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-90 disabled:hover:scale-100 inline-flex items-center justify-center gap-2"
              style={{
                background: "linear-gradient(135deg,#1f3d2e 0%,#2c5240 50%,#1f3d2e 100%)",
                boxShadow:
                  "0 1px 2px rgba(0,0,0,0.06), 0 12px 24px -10px rgba(31,61,46,0.45)",
              }}
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {settled && <CheckCircle2 className="w-4 h-4 text-emerald-300" />}
              {!submitting && !settled && (
                <>
                  Reserve my spot
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
              {submitting && "Reserving…"}
              {settled && (status === "duplicate" ? "Already on the list" : "You're in")}
            </button>
          </form>

          <p className="mt-3 text-[11.5px] text-ink-mute leading-snug">
            No spam — one email when BlendSafe launches. Your address stays
            private and is never sold.
          </p>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
